import React, { FC, useCallback, useState } from 'react';

import { Icon, Pressable, Typography } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { IPaymentMethod } from 'features/payment/types';
import { StyleSheet, View } from 'react-native';

import Currency from 'components/currency';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { getDesignToken } from 'styles/constants';

interface IPrepaidMethodBalance {
  method: IPaymentMethod;
  isDisabled?: boolean;
  isMasked?: boolean;
}

const formatCardNumber = (cardNumber: string): string =>
  (cardNumber.match(/.{1,4}/g) || []).join(' ');

const MaskDot = ({ color, size }: { color: string; size: number }) => (
  <View style={[styles.maskDot, { backgroundColor: color, width: size, height: size }]} />
);

const MaskGroup = ({ color, size }: { color: string; size: number }) => (
  <View style={styles.maskGroup}>
    {Array.from({ length: 4 }).map((_, index) => (
      <MaskDot key={index} color={color} size={size} />
    ))}
  </View>
);

const MaskedNumber = ({
  color,
  size,
  lastFour,
}: {
  color: string;
  size: number;
  lastFour?: string;
}) => (
  <View style={styles.maskNumberContainer}>
    {Array.from({ length: lastFour ? 3 : 4 }).map((_, index) => (
      <MaskGroup key={index} color={color} size={size} />
    ))}
    {lastFour && (
      <Typography color={color} testID="gift-card-last-four">
        {lastFour}
      </Typography>
    )}
  </View>
);

const ExpirationLabel = ({
  month,
  year,
  color,
  fontSize,
  lineHeight,
}: {
  month: string;
  year: string;
  color: string;
  fontSize?: React.ComponentProps<typeof Typography>['fontSize'];
  lineHeight?: React.ComponentProps<typeof Typography>['lineHeight'];
}) => (
  <Typography
    fontSize={fontSize}
    lineHeight={lineHeight}
    color={color}
    style={styles.expirationText}
    testID="gift-card-expiration"
  >
    {`${t('expire')} ${month}/${year.slice(-2)}`}
  </Typography>
);

const PrepaidMethodBalance: FC<IPrepaidMethodBalance> = ({
  method,
  isDisabled = false,
  isMasked = false,
}) => {
  const { getBalanceFromPaymentMethods } = usePaymentContext();
  const isDigitalGiftCardNumberExposedEnabled = useFlag(
    LaunchDarklyFlag.IS_DIGITAL_GIFT_CARD_NUMBER_EXPOSED_ENABLED
  );
  const [isNumberVisible, setIsNumberVisible] = useState(false);

  const toggleNumberVisibility = useCallback(() => {
    setIsNumberVisible(prev => !prev);
  }, []);

  const cardNumber = method.prepaid?.cardNumber;
  const textColor = isDisabled ? getDesignToken('text-disabled') : getDesignToken('text-default');

  const lastFour = cardNumber?.slice(-4);
  const optionText = isDigitalGiftCardNumberExposedEnabled
    ? `${t('giftCard')}${lastFour ? ` *${lastFour}` : ''} -`
    : `${t('giftCard')}`;

  const expirationData =
    isDigitalGiftCardNumberExposedEnabled &&
    method.prepaid?.expiryMonth &&
    method.prepaid?.expiryYear
      ? { month: method.prepaid.expiryMonth, year: method.prepaid.expiryYear }
      : null;

  // Legacy path: flag off or not masked
  // Flag ON: label + balance + exp date
  // Flag OFF: label + balance only
  if (!isMasked || !isDigitalGiftCardNumberExposedEnabled) {
    return (
      <View style={styles.legacyRow}>
        <Typography color={textColor} variant="bodyOne" fontFamily="body" fontSize={15}>
          {optionText}
        </Typography>
        <Currency
          amount={getBalanceFromPaymentMethods(method)}
          textProps={{ style: { margin: 0 } }}
        />
        {expirationData && (
          <ExpirationLabel
            month={expirationData.month}
            year={expirationData.year}
            color={textColor}
            fontSize={'xs'}
          />
        )}
      </View>
    );
  }

  const CardNumberRow = cardNumber ? (
    <View style={styles.maskNumberRow}>
      {isNumberVisible ? (
        <Typography fontSize="xs" color={textColor} testID="gift-card-number">
          {formatCardNumber(cardNumber)}
        </Typography>
      ) : (
        <MaskedNumber color={textColor} size={5} lastFour={lastFour} />
      )}
      <Pressable
        onPress={toggleNumberVisibility}
        testID="toggle-gift-card-number"
        hitSlop={8}
        accessibilityLabel={isNumberVisible ? 'Hide card number' : 'Show card number'}
        accessibilityRole="button"
      >
        <Icon
          variant={isNumberVisible ? 'visibility' : 'visibilityOff'}
          size="$6"
          color={getDesignToken('icon-default')}
        />
      </Pressable>
    </View>
  ) : null;

  return (
    <View style={styles.container}>
      <View style={styles.titleRow}>
        <Typography color={textColor}>{t('giftCard')}</Typography>
        {CardNumberRow}
      </View>
      <View style={styles.balanceRow}>
        <Typography fontSize="2xs" lineHeight="xs" color={textColor}>
          {t('currentBalance')}
        </Typography>
        <Currency
          amount={getBalanceFromPaymentMethods(method)}
          textProps={{ fontSize: '2xs', lineHeight: 'xs' }}
        />
      </View>
      {expirationData && (
        <ExpirationLabel
          month={expirationData.month}
          year={expirationData.year}
          color={textColor}
          fontSize="xs"
          lineHeight="md"
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
  },
  titleRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  balanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  maskDot: {
    borderRadius: 5,
  },
  maskGroup: {
    flexDirection: 'row',
    gap: 1,
    alignItems: 'center',
  },
  maskNumberContainer: {
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
  },
  maskNumberRow: {
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
  },
  legacyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  expirationText: {
    textTransform: 'capitalize',
  },
});

export default PrepaidMethodBalance;
